import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api, { resolveImage, formatUSD } from "@/lib/api";
import { toast } from "sonner";
import {
  PartyPopper, Heart, Briefcase, Users, Cake, Sparkles, Calendar, MapPin, Quote, Play, ChevronRight, Send, Check, ShoppingBag,
} from "lucide-react";

const ICONS = {
  wedding: Heart,
  birthday: Cake,
  baptism: Sparkles,
  private: PartyPopper,
  corporate: Briefcase,
  community: Users,
  other: Sparkles,
};

const isExternalVideo = (url) => url && (url.startsWith("http") || url.startsWith("//"));

export default function Events() {
  const [realizations, setRealizations] = useState([]);
  const [options, setOptions] = useState({ event_types: [], services: [] });
  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedReal, setSelectedReal] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    event_type: "wedding",
    event_date: "",
    guest_count: 100,
    budget: "",
    location: "",
    company: "",
    services: [],
    message: "",
  });

  useEffect(() => {
    api.get("/events/realizations").then((r) => setRealizations(r.data)).catch(() => {});
    api.get("/events/options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => (
    activeFilter === "all" ? realizations : realizations.filter((r) => r.event_type === activeFilter)
  ), [realizations, activeFilter]);

  const toggleService = (key) => {
    setForm((f) => ({ ...f, services: f.services.includes(key) ? f.services.filter((s) => s !== key) : [...f.services, key] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await api.post("/events/quote", { ...form, guest_count: parseInt(form.guest_count) || 0 });
      toast.success(`Demande reçue — référence ${r.data.quote_number}`, { description: "Nous vous répondons sous 24h." });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white text-[var(--dsf-text)] min-h-screen" data-testid="events-page">
      <Header />

      {/* HERO */}
      <section className="dd-pattern-leaf pt-32 pb-24 px-6 md:px-12 lg:px-20 text-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dd-mustard)] font-bold mb-4">Dakar Events</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
              Your events, <span className="italic text-[var(--dd-mustard)]">orchestrated</span> with the soul of Senegal.
            </h1>
            <p className="text-white/75 mt-6 max-w-xl text-lg leading-relaxed">
              Weddings, baptisms, birthdays, corporate gatherings, community galas — we design, produce and host your event end-to-end, with the food and the warmth of Dakar at heart.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#catering-menu" className="btn-mustard" data-testid="hero-cta-catering"><Send size={14} /> See catering menu</a>
              <a href="#devis" className="btn-pill-outline !text-white !border-white/30 hover:!bg-white/10"><Play size={14} /> Request a quote</a>
            </div>
            <div className="grid grid-cols-3 gap-6 mt-12 max-w-md">
              <div><div className="text-3xl font-serif text-[var(--dd-mustard)]">120+</div><div className="text-xs text-white/60 mt-1">Events produced</div></div>
              <div><div className="text-3xl font-serif text-[var(--dd-mustard)]">15k+</div><div className="text-xs text-white/60 mt-1">Guests served</div></div>
              <div><div className="text-3xl font-serif text-[var(--dd-mustard)]">4.9/5</div><div className="text-xs text-white/60 mt-1">Average rating</div></div>
            </div>
          </div>
          <div className="lg:col-span-5 relative hidden lg:block">
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden ring-4 ring-[var(--dd-mustard)]/30">
              <img src="https://images.unsplash.com/photo-1519741497674-611481863552?w=900" alt="Dakar Events" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white text-[var(--dd-green)] px-6 py-4 rounded-2xl shadow-2xl max-w-[240px]">
              <div className="text-xs font-bold uppercase tracking-wider text-[var(--dd-mustard-dark)]">Turnkey</div>
              <div className="text-sm font-semibold mt-1">Catering · Decor · Music · Food Truck · Coordination</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATERING MENU — trays for pickup */}
      <CateringMenu />

      {/* SERVICES BLOCKS */}
      <section className="px-6 md:px-12 lg:px-20 py-20 bg-[var(--dd-cream)]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-12">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3 font-bold">Nos services</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)] leading-tight">Pour chaque moment, une production sur-mesure.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {options.event_types.filter((t) => t.key !== "other").map((t) => {
              const Icon = ICONS[t.key] || Sparkles;
              return (
                <div key={t.key} className="bg-white rounded-3xl p-7 border border-black/5 hover:-translate-y-1 transition-all" data-testid={`service-card-${t.key}`}>
                  <div className="w-12 h-12 rounded-2xl bg-[var(--dd-green-soft)] text-[var(--dd-green)] flex items-center justify-center mb-5">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-serif text-2xl text-[var(--dd-green)] mb-2">{t.label_fr}</h3>
                  <p className="text-sm text-[var(--dsf-text-muted)] leading-relaxed">
                    {t.key === "wedding" && "Cérémonies traditionnelles, mariages mixtes, henna nights — un seul interlocuteur pour toute votre célébration."}
                    {t.key === "birthday" && "Anniversaires intimistes ou grand format, sweet 16, soirées surprise — un cadre festif inoubliable."}
                    {t.key === "baptism" && "Baptêmes en famille avec menu sur-mesure, décoration douce, ambiance chaleureuse."}
                    {t.key === "private" && "Soirées privées, dîners de gala, after-party — un univers entièrement personnalisé."}
                    {t.key === "corporate" && "Séminaires, lancements, fêtes d'entreprise — la touche street food de Dakar qui marque les esprits."}
                    {t.key === "community" && "Galas culturels, soirées diaspora, fêtes nationales — l'expérience africaine authentique à grande échelle."}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REALIZATIONS / PORTFOLIO */}
      <section id="realisations" className="px-6 md:px-12 lg:px-20 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div className="max-w-xl">
              <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3 font-bold">Nos réalisations</div>
              <h2 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)] leading-tight">Quelques moments dont nous sommes fiers.</h2>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="portfolio-filters">
              <button onClick={() => setActiveFilter("all")} className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition ${activeFilter === "all" ? "bg-[var(--dd-green)] text-white" : "bg-[var(--dd-bone)] text-[var(--dd-green)]"}`} data-testid="portfolio-filter-all">All</button>
              {options.event_types.filter((t) => t.key !== "other").map((t) => (
                <button key={t.key} onClick={() => setActiveFilter(t.key)} className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition ${activeFilter === t.key ? "bg-[var(--dd-green)] text-white" : "bg-[var(--dd-bone)] text-[var(--dd-green)] hover:bg-[var(--dd-green-soft)]"}`} data-testid={`portfolio-filter-${t.key}`}>{t.label_fr}</button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-[var(--dsf-text-muted)]">Aucune réalisation pour ce filtre pour le moment.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelectedReal(r)}
                  className="group text-left bg-[var(--dd-bone)] rounded-3xl overflow-hidden hover:-translate-y-1 transition-all"
                  data-testid={`realization-card-${r.id}`}
                >
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img src={resolveImage(r.thumbnail_url) || r.photos?.[0]} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {r.video_url && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                        <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center text-[var(--dd-green)]">
                          <Play size={22} fill="currentColor" />
                        </div>
                      </div>
                    )}
                    {r.featured && (
                      <div className="absolute top-3 left-3 bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full">Featured</div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] font-bold mb-2">{r.event_type}</div>
                    <h3 className="font-serif text-xl text-[var(--dd-green)] mb-2 leading-snug">{r.title_fr || r.title}</h3>
                    <div className="flex items-center gap-3 text-xs text-[var(--dsf-text-muted)]">
                      {r.location && <span className="flex items-center gap-1"><MapPin size={11} /> {r.location}</span>}
                      {r.guest_count > 0 && <span className="flex items-center gap-1"><Users size={11} /> {r.guest_count}</span>}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS - from realizations */}
      {realizations.some((r) => r.testimonial_text) && (
        <section className="px-6 md:px-12 lg:px-20 py-20 bg-[var(--dd-green-soft)]">
          <div className="max-w-7xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3 font-bold">Ils nous ont fait confiance</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)] mb-10 max-w-2xl leading-tight">La parole de nos clients.</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {realizations.filter((r) => r.testimonial_text).slice(0, 3).map((r) => (
                <div key={r.id} className="bg-white rounded-3xl p-7 relative" data-testid={`testimonial-${r.id}`}>
                  <Quote size={28} className="text-[var(--dd-mustard)] mb-3" />
                  <p className="text-[var(--dsf-text)] leading-relaxed text-sm">"{r.testimonial_text}"</p>
                  <div className="mt-5 pt-4 border-t border-black/5">
                    <div className="font-semibold text-[var(--dd-green)]">{r.testimonial_author}</div>
                    <div className="text-xs text-[var(--dsf-text-muted)]">{r.title_fr || r.title}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* QUOTE FORM */}
      <section id="devis" className="px-6 md:px-12 lg:px-20 py-24 bg-[var(--dd-cream)]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3 font-bold">Demande de devis</div>
            <h2 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)] leading-tight max-w-2xl mx-auto">Parlez-nous de votre événement.</h2>
            <p className="text-[var(--dsf-text-muted)] mt-4 max-w-xl mx-auto">Réponse personnalisée sous 24h. Aucune carte requise. C'est gratuit et sans engagement.</p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-3xl p-12 text-center border-2 border-[var(--dd-green)]/20" data-testid="quote-success">
              <div className="w-20 h-20 rounded-full bg-[var(--dd-green)] text-white flex items-center justify-center mx-auto mb-5">
                <Check size={36} />
              </div>
              <h3 className="font-serif text-3xl text-[var(--dd-green)] mb-3">Demande envoyée !</h3>
              <p className="text-[var(--dsf-text-muted)] max-w-md mx-auto">Notre équipe vous contactera sous 24h avec un devis personnalisé pour votre événement.</p>
              <button onClick={() => { setSubmitted(false); setForm({ ...form, message: "", customer_name: "", customer_email: "", customer_phone: "" }); }} className="btn-pill-outline mt-6">Soumettre une autre demande</button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-3xl p-6 md:p-10 space-y-6" data-testid="quote-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Nom complet *" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="quote-name" />
                <Field label="Email *" type="email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} testid="quote-email" />
                <Field label="Téléphone *" value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} testid="quote-phone" />
                <Field label="Société (optionnel)" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="quote-company" />
              </div>

              <div>
                <Label>Type d'événement *</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2" data-testid="quote-event-types">
                  {options.event_types.map((t) => {
                    const Icon = ICONS[t.key] || Sparkles;
                    const active = form.event_type === t.key;
                    return (
                      <button type="button" key={t.key} onClick={() => setForm({ ...form, event_type: t.key })} className={`flex items-center gap-2 p-3 rounded-xl border-2 text-xs font-semibold uppercase tracking-wider transition ${active ? "border-[var(--dd-green)] bg-[var(--dd-green-soft)] text-[var(--dd-green)]" : "border-black/5 text-[var(--dsf-text-muted)] hover:border-[var(--dd-green)]"}`} data-testid={`quote-type-${t.key}`}>
                        <Icon size={14} /><span className="truncate">{t.label_fr}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <Field label="Date souhaitée *" type="date" value={form.event_date} onChange={(v) => setForm({ ...form, event_date: v })} testid="quote-date" />
                <Field label="Nombre d'invités *" type="number" value={form.guest_count} onChange={(v) => setForm({ ...form, guest_count: v })} testid="quote-guests" />
                <div>
                  <Label>Budget estimé</Label>
                  <select value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm bg-white mt-2 focus:border-[var(--dd-green)]" data-testid="quote-budget">
                    <option value="">— Indicatif —</option>
                    <option value="<$5k">Moins de $5 000</option>
                    <option value="$5k-$10k">$5 000 – $10 000</option>
                    <option value="$10k-$25k">$10 000 – $25 000</option>
                    <option value="$25k-$50k">$25 000 – $50 000</option>
                    <option value=">$50k">Plus de $50 000</option>
                  </select>
                </div>
              </div>

              <Field label="Lieu de l'événement *" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="quote-location" placeholder="ex: Brooklyn NY ou 'lieu à définir'" />

              <div>
                <Label>Services souhaités</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2" data-testid="quote-services">
                  {options.services.map((s) => {
                    const checked = form.services.includes(s.key);
                    return (
                      <label key={s.key} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${checked ? "border-[var(--dd-green)] bg-[var(--dd-green-soft)]" : "border-black/5 hover:border-[var(--dd-green)]"}`} data-testid={`quote-service-${s.key}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleService(s.key)} className="accent-[var(--dd-green)]" />
                        <span className="text-sm font-medium text-[var(--dd-green)]">{s.label_fr}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label>Décrivez-nous votre projet *</Label>
                <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm bg-white mt-2 focus:border-[var(--dd-green)]" placeholder="Thème, ambiance, contraintes, attentes particulières…" data-testid="quote-message" />
              </div>

              <button type="submit" disabled={submitting} className="btn-mustard w-full justify-center !py-4 disabled:opacity-50" data-testid="quote-submit">
                {submitting ? "Envoi…" : (<><Send size={16} /> Envoyer ma demande</>)}
              </button>
              <p className="text-[10px] text-center text-[var(--dsf-text-muted)]">En soumettant ce formulaire vous acceptez d'être contacté(e) par notre équipe événementiel.</p>
            </form>
          )}
        </div>
      </section>

      {/* MODAL — Realization details */}
      {selectedReal && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelectedReal(null)} data-testid="realization-modal">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {selectedReal.video_url ? (
              <div className="aspect-video w-full bg-black rounded-t-3xl overflow-hidden">
                {isExternalVideo(selectedReal.video_url) ? (
                  <iframe src={selectedReal.video_url} className="w-full h-full" allowFullScreen title={selectedReal.title} />
                ) : (
                  <video src={resolveImage(selectedReal.video_url)} controls className="w-full h-full" />
                )}
              </div>
            ) : (
              <img src={resolveImage(selectedReal.thumbnail_url)} alt={selectedReal.title} className="w-full aspect-video object-cover rounded-t-3xl" />
            )}
            <div className="p-6 md:p-10">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] font-bold mb-2">{selectedReal.event_type}</div>
              <h3 className="font-serif text-3xl md:text-4xl text-[var(--dd-green)] mb-4">{selectedReal.title_fr || selectedReal.title}</h3>
              <div className="flex flex-wrap gap-5 text-sm text-[var(--dsf-text-muted)] mb-5">
                {selectedReal.date_held && <span className="flex items-center gap-1"><Calendar size={13} /> {selectedReal.date_held}</span>}
                {selectedReal.location && <span className="flex items-center gap-1"><MapPin size={13} /> {selectedReal.location}</span>}
                {selectedReal.guest_count > 0 && <span className="flex items-center gap-1"><Users size={13} /> {selectedReal.guest_count} invités</span>}
              </div>
              <p className="text-[var(--dsf-text)] leading-relaxed">{selectedReal.description_fr || selectedReal.description}</p>

              {selectedReal.photos?.length > 0 && (
                <div className="mt-8">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] font-bold mb-3">Galerie</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedReal.photos.map((p, i) => (
                      <img key={i} src={resolveImage(p)} alt={`${selectedReal.title} ${i + 1}`} className="aspect-square object-cover rounded-2xl" />
                    ))}
                  </div>
                </div>
              )}

              {selectedReal.testimonial_text && (
                <div className="mt-8 p-6 bg-[var(--dd-green-soft)] rounded-2xl">
                  <Quote size={22} className="text-[var(--dd-mustard)] mb-2" />
                  <p className="italic text-[var(--dsf-text)]">"{selectedReal.testimonial_text}"</p>
                  <div className="font-semibold text-[var(--dd-green)] mt-3">{selectedReal.testimonial_author}</div>
                </div>
              )}

              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#devis" onClick={() => setSelectedReal(null)} className="btn-mustard"><Send size={14} /> Demander un devis similaire</a>
                <button onClick={() => setSelectedReal(null)} className="btn-pill-outline">Fermer</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] font-bold">{children}</label>
);

const Field = ({ label, value, onChange, type = "text", testid, placeholder }) => (
  <div>
    <Label>{label}</Label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full border border-black/10 rounded-xl px-4 py-3 text-sm bg-white mt-2 focus:border-[var(--dd-green)]"
      data-testid={testid}
      required={label.includes("*")}
    />
  </div>
);


/* =================== CATERING MENU =================== */
function CateringMenu() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products", { params: { universe: "dakar_events" } })
      .then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, []);

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map();
    [...products]
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .forEach((p) => {
        const cat = p.category || "Other";
        if (!map.has(cat)) map.set(cat, []);
        map.get(cat).push(p);
      });
    return Array.from(map.entries());
  }, [products]);

  if (loading) return null;
  if (products.length === 0) return null;

  return (
    <section id="catering-menu" className="px-6 md:px-12 lg:px-20 py-20 bg-white" data-testid="catering-menu-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dd-mustard-dark)] font-bold mb-3">CATERING · PICKUP ONLY</div>
          <h2 className="font-serif text-4xl md:text-5xl text-[var(--dd-green)] leading-tight">Order Catering Trays</h2>
          <p className="text-[var(--dsf-text-secondary)] mt-4 max-w-2xl mx-auto">
            Browse our catering trays, add them to your cart and pick them up at the restaurant. Need full event production? <a href="#devis" className="text-[var(--dd-mustard-dark)] font-semibold underline">Request a quote</a> below.
          </p>
        </div>

        <div className="space-y-12">
          {grouped.map(([category, items]) => (
            <div key={category} data-testid={`catering-cat-${category.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-3 mb-5">
                <h3 className="font-serif text-2xl md:text-3xl text-[var(--dd-green)]">{category}</h3>
                <div className="flex-1 h-px bg-[var(--dd-green)]/15" />
                <div className="text-xs uppercase tracking-wider text-[var(--dsf-text-muted)]">{items.length} {items.length > 1 ? "items" : "item"}</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((p) => (
                  <Link
                    to={`/produit/${p.slug}`}
                    key={p.id}
                    className="group relative flex items-center gap-4 p-4 rounded-2xl border border-black/5 hover:border-[var(--dd-mustard)] hover:shadow-lg transition-all bg-[var(--dd-bone)]/50 hover:bg-white"
                    data-testid={`catering-item-${p.slug}`}
                  >
                    <img src={resolveImage(p.image)} alt={p.name} className="w-20 h-20 rounded-xl object-cover shrink-0" loading="lazy" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[var(--dd-green)] text-sm leading-snug">{p.name}</div>
                      {p.short_description && (
                        <div className="text-xs text-[var(--dsf-text-muted)] mt-1 line-clamp-2">{p.short_description}</div>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="font-bold text-[var(--dd-mustard-dark)] text-base">{formatUSD(p.price)}</span>
                        {p.available === false && (
                          <span className="text-[9px] uppercase font-bold bg-[var(--dd-coral)] text-white px-1.5 py-0.5 rounded">Sold out</span>
                        )}
                      </div>
                    </div>
                    <div className="w-9 h-9 rounded-full bg-[var(--dd-mustard)] text-[var(--dd-green-deep)] flex items-center justify-center opacity-0 group-hover:opacity-100 transition shrink-0 shadow-md">
                      <ShoppingBag size={15} />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 text-center">
          <Link to="/univers/dakar_events" className="btn-mustard inline-flex" data-testid="catering-see-all">
            See full catering menu <ChevronRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
